// Google Publisher Tag (GPT) loader + slot lifecycle for Google Ad Manager.
//
// Every entry point is a no-op unless ads are enabled for the environment (seavien-ads-enabled meta +
// a valid network code) AND the visitor granted the "ads" cookie-consent category. The heavy external
// gpt.js is injected lazily — only the first time a slot is actually displayed (an AdSlot scrolls near
// the viewport) — so it never competes with first paint or hydration.
//
// Slots are defined + displayed individually on approach (not single-request) so each AdSlot is
// self-contained and cleans itself up on unmount, which keeps soft navigation from leaking slots.
// See designs/04-live/2026-06-22-google-ad-manager-display-ads-design.md.

import * as CookieConsent from "vanilla-cookieconsent";

import logger from "@/lib/logger";
import { MetaHandler } from "@/main/common/MetaHandler";

import { AD_PLACEMENTS, AdPlacementKey, AdSize } from "./adUnits";

const GPT_SRC = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";

// GPT's API is loosely typed; we interact through a minimal structural view of `window.googletag`.
type GptCommandQueue = { push: (fn: () => void) => void };
type GoogleTag = {
    cmd: GptCommandQueue;
    [key: string]: unknown;
};

declare global {
    interface Window {
        googletag?: GoogleTag;
    }
}

let scriptInjected = false;
let servicesConfigured = false;
// Tracks defined GPT slots by element id so we can destroy them on unmount.
const definedSlots = new Map<string, unknown>();

/** Whether the visitor granted the "ads" consent category. Fail-closed on any error. */
export function adsConsentGranted(): boolean {
    try {
        return CookieConsent.acceptedCategory("ads");
    } catch {
        return false;
    }
}

/**
 * Whether an ad may be loaded at all: ads enabled for this environment, a network code present, and
 * "ads" consent granted. Both AdSlot rendering and GPT loading gate on this.
 */
export function areAdsAllowed(): boolean {
    return (
        MetaHandler.isAdsEnabled() &&
        Boolean(MetaHandler.getAdsNetworkCode()) &&
        adsConsentGranted()
    );
}

function getGoogletag(): GoogleTag {
    const existing = window.googletag;
    if (existing && existing.cmd) {
        return existing;
    }
    const created: GoogleTag = { cmd: [] as unknown as GptCommandQueue };
    window.googletag = created;
    return created;
}

function injectGptScript(): void {
    if (scriptInjected || typeof document === "undefined") {
        return;
    }
    scriptInjected = true;
    const script = document.createElement("script");
    script.async = true;
    script.src = GPT_SRC;
    script.crossOrigin = "anonymous";
    script.addEventListener("error", () => {
        logger.error("[ads] Failed to load the Google Publisher Tag (gpt.js).");
    });
    document.head.appendChild(script);
}

function configureServices(): void {
    if (servicesConfigured) {
        return;
    }
    servicesConfigured = true;
    const googletag = getGoogletag();
    googletag.cmd.push(() => {
        try {
            const pubads = (googletag.pubads as () => any)();
            // Collapse a slot's reserved box only when it returns no creative (reserve-then-collapse),
            // so an unfilled slot doesn't leave a permanent gap while a filled one never shifts.
            pubads.collapseEmptyDivs();
            // Honor Consent Mode: restrict data processing when "ads" consent is not granted.
            if (typeof pubads.setPrivacySettings === "function") {
                pubads.setPrivacySettings({ restrictDataProcessing: !adsConsentGranted() });
            }
            (googletag.enableServices as () => void)();
        } catch (error) {
            logger.error("[ads] Failed to configure GPT services.", error);
        }
    });
}

/** Inject gpt.js + configure services once. No-op unless ads are allowed. */
export function ensureGptLoaded(): void {
    if (!areAdsAllowed()) {
        return;
    }
    injectGptScript();
    configureServices();
}

// GPT accepts our AdSize values directly ([w,h] tuples and the "fluid" literal).
function toGptSizes(sizes: AdSize[]): AdSize[] {
    return sizes;
}

/**
 * Define and display the ad slot bound to `elementId`. Idempotent per element id. No-op unless ads
 * are allowed. Safe to call from an IntersectionObserver callback.
 */
export function displayAdSlot(elementId: string, placement: AdPlacementKey): void {
    if (!areAdsAllowed()) {
        return;
    }
    const definition = AD_PLACEMENTS[placement];
    const networkCode = MetaHandler.getAdsNetworkCode();
    if (!definition || !networkCode) {
        return;
    }

    ensureGptLoaded();
    const googletag = getGoogletag();
    googletag.cmd.push(() => {
        try {
            if (definedSlots.has(elementId)) {
                return;
            }
            const adUnitPath = `/${networkCode}/${definition.adUnitPath}`;
            const slot = (googletag.defineSlot as (path: string, size: AdSize[], div: string) => any)(
                adUnitPath,
                toGptSizes(definition.sizes),
                elementId,
            );
            if (!slot) {
                logger.warn(`[ads] defineSlot returned null for ${elementId}.`);
                return;
            }

            if (definition.sizeMapping.length > 0) {
                const mappingBuilder = (googletag.sizeMapping as () => any)();
                definition.sizeMapping.forEach((entry) => {
                    mappingBuilder.addSize(entry.viewport, toGptSizes(entry.sizes));
                });
                slot.defineSizeMapping(mappingBuilder.build());
            }

            slot.addService((googletag.pubads as () => any)());
            definedSlots.set(elementId, slot);
            (googletag.display as (id: string) => void)(elementId);
        } catch (error) {
            logger.error(`[ads] Failed to display ad slot ${elementId}.`, error);
        }
    });
}

/** Destroy the GPT slot bound to `elementId` (call on AdSlot unmount). */
export function destroyAdSlot(elementId: string): void {
    const slot = definedSlots.get(elementId);
    if (!slot) {
        return;
    }
    definedSlots.delete(elementId);
    const googletag = window.googletag;
    if (!googletag || !googletag.cmd) {
        return;
    }
    googletag.cmd.push(() => {
        try {
            const destroySlots = googletag.destroySlots as ((slots: unknown[]) => boolean) | undefined;
            if (typeof destroySlots === "function") {
                destroySlots([slot]);
            }
        } catch (error) {
            logger.error(`[ads] Failed to destroy ad slot ${elementId}.`, error);
        }
    });
}

/** Test-only reset of module state. */
export function resetAdsStateForTests(): void {
    scriptInjected = false;
    servicesConfigured = false;
    definedSlots.clear();
}
